import UserAvatar from './UserAvatar';
import { OrganizationAgentChat } from '@features/ai-chat/OrganizationAgentChat';
import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  MouseEventHandler,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Box,
  Card,
  CardActions,
  CardContent,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Switch,
  Typography,
  Grid,
  ButtonBase,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { setLocale, useLocale } from '@client/providers/LocaleProvider';
import { isAdmin } from '@shared/helpers/user.helpers';
import {
  OPEN_AI_ALLOWED_MODELS,
  isAllowedOpenAiModel,
  type OpenAiAllowedModels,
} from '@shared/models/ai/ai-models.model';
import {
  readStoredAiChatModel,
  writeStoredAiChatModel,
} from '@features/ai-chat/ai-chat-model.storage';
import { AI_CHARACTERS } from '@features/ai-characters/AiCharacters';
import {
  RANDOM_AI_CHARACTER_ID,
  readStoredAiCharacterId,
  writeStoredAiCharacterId,
} from '@features/ai-characters/ai-character.storage';
import { AI_THEMES } from '@features/ai-characters/ai-theme.model';
import {
  AUTO_AI_THEME_ID,
  readStoredAiThemeId,
  writeStoredAiThemeId,
} from '@features/ai-characters/ai-theme.storage';
import { SENTRY_TEST_ADMIN_PATH } from '@shared/helpers/sentry-test.helpers';
import type { ReportCoverImageRequest } from '@client/helpers/report-cover-image-status.helpers';
import amplitudeLog from '@client/amplitude';
import { AMP_APP_LOGOUT, AMP_PROJECT_EXPORT } from '@shared/constants';
import { usePromiseSnackbar } from '@client/hooks/use-snackbar.hooks';
import { validateFiniteNumber } from '@shared/validation/number.validation';
import { capitalize } from 'lodash';
import {
  useIsProjectList,
  useProjectNavigation,
} from '@client/hooks/use-router.hooks';
import { User } from '@shared/models/user.model';
import { Page, type DesignImportRequest } from '@client/store/ui/ui.model';
import { LinkButton, Button } from '@ui/buttons';
import {
  getProject,
  useUpdateProject,
  useExportProject,
  useForceEnrichProject,
} from '@client/store/project';
import { useIsConfirmed } from '@client/hooks/use-confirm.hook';
import { useImportProjectDialogStore } from '@features/projects/ImportProjectDialog';
import { FfuImportCards } from '@features/projects/FfuImport/FfuImports';
import { useUIState } from '@client/store/ui';
import { clearProposalsInProject } from '@shared/helpers/proposal-migration.helpers';
import { useReportPrintLink } from '@client/hooks/use-report-print-link.hook';

const REPORT_PRINT_PAGE_LABEL = 'Report - Print page';

const useStyles = makeStyles()((theme) => ({
  aiChatModel: {
    minWidth: 180,
  },
  aiChatModelMenu: {
    // Modal writes z-index as an inline style. !important keeps the menu
    // above the profile popover.
    zIndex: `${theme.zIndex.tooltip} !important`,
  },
}));

type AiChatModelChangeHandler = NonNullable<
  React.ComponentProps<typeof Select<OpenAiAllowedModels>>['onChange']
>;

interface ProfileMenuProps {
  user: User;
}

const ProfileMenu: React.FC<ProfileMenuProps> = ({ user }) => {
  const { classes } = useStyles();
  const navigateTo = useProjectNavigation();
  const [, localeDispatch] = useLocale();
  const updateProject = useUpdateProject();
  const exportProject = useExportProject();
  const forceEnrichProject = useForceEnrichProject();
  const promiseSnackbar = usePromiseSnackbar();
  const confirm = useIsConfirmed();

  const auth0 = useAuth0();
  const isProjectList = useIsProjectList();
  const openImportProjectDialog = useImportProjectDialogStore(
    (state) => state.setOpen,
  );
  const {
    selectedPage,
    showRenderDebug,
    setShowRenderDebug,
    setReportCoverImageRequest,
    setDesignImportRequest,
  } = useUIState(
    'selectedPage',
    'showRenderDebug',
    'setShowRenderDebug',
    'setReportCoverImageRequest',
    'setDesignImportRequest',
  );

  useEffect(() => {
    setLocale('en-US', localeDispatch);
  }, [localeDispatch]);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [aiChatModel, setAiChatModel] = useState<OpenAiAllowedModels>(
    readStoredAiChatModel,
  );
  const [aiCharacterId, setAiCharacterId] = useState(readStoredAiCharacterId);
  const [aiThemeId, setAiThemeId] = useState(readStoredAiThemeId);
  const open = useMemo(() => !!anchorEl, [anchorEl]);
  const reportPrintLink = useReportPrintLink({
    enabled: open && !isProjectList && isAdmin(user),
  });

  const navigateToPage = useCallback(
    (page: Page) => {
      navigateTo({ page });
    },
    [navigateTo],
  );

  const handleIconClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    (e) => setAnchorEl(e.currentTarget),
    [setAnchorEl],
  );

  const handlePopoverClick: MouseEventHandler<HTMLDivElement> = useCallback(
    () => setAnchorEl(null),
    [setAnchorEl],
  );

  const handleLogoutClick: MouseEventHandler<HTMLButtonElement> =
    useCallback(() => {
      if (user) {
        amplitudeLog(AMP_APP_LOGOUT);
      }

      window.sessionStorage.setItem('isLoggedIn', 'false');

      auth0.logout({
        logoutParams: { returnTo: window.location.origin },
      });
    }, [user, auth0]);

  const handleCreateFromFileClick: MouseEventHandler<HTMLButtonElement> =
    useCallback(() => {
      openImportProjectDialog(true);
    }, [openImportProjectDialog]);

  const handleExportProjectClick: MouseEventHandler<HTMLButtonElement> =
    useCallback(() => {
      const project = getProject();
      const projectId = project?.id;
      if (projectId == null) return;
      amplitudeLog(AMP_PROJECT_EXPORT, { ProjectId: projectId });
      promiseSnackbar(exportProject(validateFiniteNumber(projectId))).catch(
        () => {},
      );
    }, [exportProject, promiseSnackbar]);

  const handleForceEnrichClick: MouseEventHandler<HTMLButtonElement> =
    useCallback(() => {
      promiseSnackbar(forceEnrichProject(), {
        successMessage: 'Project enriched',
      }).catch(() => {});
    }, [forceEnrichProject, promiseSnackbar]);

  const handleClearProposalsClick: MouseEventHandler<HTMLButtonElement> =
    useCallback(async () => {
      const project = getProject();
      const confirmed = await confirm({
        title: 'Clear proposals',
        description:
          'This will remove proposal actions from all building versions and leave one empty proposal per version.',
        confirmationText: 'Clear',
      });
      if (confirmed) {
        // TODO(replay): express as ProjectChange so a 422 race can be replayed.
        await updateProject({
          kind: 'snapshot',
          project: clearProposalsInProject(project),
        });
      }
    }, [confirm, updateProject]);

  /**
   * Navigate to Report if needed, then let the report controller open generate or remove.
   */
  const requestReportCoverImage = useCallback(
    (request: Exclude<ReportCoverImageRequest, 'none'>) => {
      if (selectedPage !== Page.Report) navigateToPage(Page.Report);
      setReportCoverImageRequest(request);
    },
    [navigateToPage, selectedPage, setReportCoverImageRequest],
  );

  const handleAiChatModelChange = useCallback<AiChatModelChangeHandler>(
    (event) => {
      const next = event.target.value;
      if (!isAllowedOpenAiModel(next)) return;
      setAiChatModel(next);
      writeStoredAiChatModel(next);
    },
    [],
  );

  const handleAiCharacterChange = useCallback(
    (event: { target: { value: string } }) => {
      setAiCharacterId(event.target.value);
      writeStoredAiCharacterId(event.target.value);
    },
    [],
  );

  const handleAiThemeChange = useCallback(
    (event: { target: { value: string } }) => {
      setAiThemeId(event.target.value);
      writeStoredAiThemeId(event.target.value);
    },
    [],
  );

  /**
   * Navigate to Design if needed, then let the map open the import flow.
   */
  const requestDesignImport = useCallback(
    (request: Exclude<DesignImportRequest, 'none'>) => {
      if (selectedPage !== Page.Design) navigateToPage(Page.Design);
      setDesignImportRequest(request);
    },
    [navigateToPage, selectedPage, setDesignImportRequest],
  );

  if (!user) {
    return null;
  }

  return (
    <Box data-keep-project-selector-open>
      <ButtonBase onClick={handleIconClick} sx={{ borderRadius: '50%' }}>
        <UserAvatar user={user} size={24} />
      </ButtonBase>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClick={handlePopoverClick}
        // The project-list agent and FFU runs are followed while the menu is closed.
        keepMounted={isProjectList || isAdmin(user)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        <Card>
          <Box
            sx={{
              p: 2,
            }}
          >
            <CardContent>
              <Grid
                container
                spacing={1}
                sx={{
                  minWidth: '200px',
                }}
              >
                <Grid size={3}>
                  <UserAvatar user={user} />
                </Grid>
                <Grid size={9}>
                  <Typography variant="body2">
                    <b>{capitalize(user.nickname)}</b>
                  </Typography>
                  <Typography variant="body2">{user.name}</Typography>
                </Grid>
              </Grid>
            </CardContent>
            {isProjectList && (
              <CardContent>
                {/* The popover closes on any click; keep it open while handling FFUs. */}
                <Box onClick={(event) => event.stopPropagation()}>
                  <OrganizationAgentChat />
                </Box>
              </CardContent>
            )}
            {isAdmin(user) && (
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'start',
                    gap: 2,
                  }}
                >
                  <Typography variant="h6">Admin Area</Typography>
                  {!isProjectList && (
                    <Box
                      sx={{ alignSelf: 'stretch', width: 380 }}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <FfuImportCards />
                    </Box>
                  )}
                  {/* The popover closes on any click; keep it open while picking a model. */}
                  <FormControl
                    className={classes.aiChatModel}
                    size="small"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <InputLabel id="ai-chat-model-label">
                      AI chat model
                    </InputLabel>
                    <Select<OpenAiAllowedModels>
                      labelId="ai-chat-model-label"
                      label="AI chat model"
                      value={aiChatModel}
                      onChange={handleAiChatModelChange}
                      MenuProps={{
                        className: classes.aiChatModelMenu,
                      }}
                    >
                      {OPEN_AI_ALLOWED_MODELS.map((model) => (
                        <MenuItem key={model} value={model}>
                          {model}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl
                    className={classes.aiChatModel}
                    size="small"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <InputLabel id="ai-character-label">
                      AI character
                    </InputLabel>
                    <Select<string>
                      labelId="ai-character-label"
                      label="AI character"
                      value={aiCharacterId}
                      onChange={handleAiCharacterChange}
                      MenuProps={{
                        className: classes.aiChatModelMenu,
                      }}
                    >
                      <MenuItem value={RANDOM_AI_CHARACTER_ID}>Random</MenuItem>
                      {AI_CHARACTERS.map(({ id, name }) => (
                        <MenuItem key={id} value={id}>
                          {name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl
                    className={classes.aiChatModel}
                    size="small"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <InputLabel id="ai-theme-label">AI theme</InputLabel>
                    <Select<string>
                      labelId="ai-theme-label"
                      label="AI theme"
                      value={aiThemeId}
                      onChange={handleAiThemeChange}
                      MenuProps={{
                        className: classes.aiChatModelMenu,
                      }}
                    >
                      <MenuItem value={AUTO_AI_THEME_ID}>
                        Auto (season or holiday)
                      </MenuItem>
                      {AI_THEMES.map(({ id, name }) => (
                        <MenuItem key={id} value={id}>
                          {name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => navigateToPage(Page.DataTables)}
                  >
                    Data-tables
                  </Button>

                  <Button
                    size="small"
                    variant="text"
                    onClick={handleCreateFromFileClick}
                  >
                    Create from file
                  </Button>
                  {!isProjectList && (
                    <Button
                      size="small"
                      variant="text"
                      onClick={handleExportProjectClick}
                    >
                      Export project
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => navigateToPage(Page.Calculations)}
                  >
                    Calculations
                  </Button>
                  <LinkButton
                    size="small"
                    variant="text"
                    to="/admin/ai-comments"
                  >
                    AI Admin
                  </LinkButton>
                  <LinkButton
                    size="small"
                    variant="text"
                    to="/admin/components"
                  >
                    Components
                  </LinkButton>
                  <LinkButton
                    size="small"
                    variant="text"
                    to={SENTRY_TEST_ADMIN_PATH}
                  >
                    Sentry test
                  </LinkButton>
                  {!isProjectList && (
                    <Button
                      size="small"
                      variant="text"
                      onClick={handleForceEnrichClick}
                    >
                      Force enrich
                    </Button>
                  )}
                  {!isProjectList && (
                    <Button
                      size="small"
                      variant="text"
                      onClick={handleClearProposalsClick}
                    >
                      Clear proposals
                    </Button>
                  )}
                  {reportPrintLink ? (
                    <LinkButton
                      size="small"
                      variant="text"
                      to={reportPrintLink}
                      openInNewTab
                    >
                      {REPORT_PRINT_PAGE_LABEL}
                    </LinkButton>
                  ) : (
                    <Button size="small" variant="text" disabled>
                      {REPORT_PRINT_PAGE_LABEL}
                    </Button>
                  )}
                  {!isProjectList && (
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => requestReportCoverImage('create')}
                    >
                      Report - Create cover image
                    </Button>
                  )}
                  {!isProjectList && (
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => requestReportCoverImage('remove')}
                    >
                      Report - Remove cover image
                    </Button>
                  )}
                  {!isProjectList && (
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => requestDesignImport('pdf')}
                    >
                      Import PDF
                    </Button>
                  )}
                  {!isProjectList && (
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => requestDesignImport('geometry-ai')}
                    >
                      Import geometry with AI
                    </Button>
                  )}
                  <Divider flexItem />
                  {/* The popover closes on any click; keep it open for a toggle. */}
                  <FormControlLabel
                    onClick={(event) => event.stopPropagation()}
                    control={
                      <Switch
                        size="small"
                        checked={showRenderDebug}
                        onChange={(_event, checked) =>
                          setShowRenderDebug(checked)
                        }
                      />
                    }
                    label="Design view: Render settings"
                    slotProps={{ typography: { variant: 'body2' } }}
                  />
                </Box>
              </CardContent>
            )}
            <CardActions sx={{ justifyContent: 'end' }}>
              <Button
                color="primary"
                variant="text"
                size="small"
                onClick={handleLogoutClick}
              >
                Logout
              </Button>
            </CardActions>
          </Box>
        </Card>
      </Popover>
    </Box>
  );
};

export default ProfileMenu;
